/**
 * FeedbackHelper
 *
 * Like / dislike feedback on a chat answer: toggling the thumb icons, showing the
 * optional "reason" input, and persisting the result via `apiService.saveChatFeedback`.
 *
 * `saveFeedback` optimistically updates the chat object and rolls the four
 * previous values back if the API reports failure or errors out. It also mirrors
 * the new state onto the in-memory `allChatList` so the history sidebar stays in sync.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged. No bare `this` existed in this cluster.
 */

type FeedbackChat = {
  question: string;
  response: string;
  messageId?: string;
  isLiked?: boolean | null;
  feedbackSaved?: boolean;
  showLikeInput?: boolean;
  likeReason?: string;
  showDislikeInput?: boolean;
  dislikeReason?: string;
};

export class FeedbackHelper {

  static onLikeClick(comp: any, chat: FeedbackChat) {
    if (!chat.messageId || !comp.selectedChatId) {
      comp.showToast('Cannot save feedback: Message ID is not available', 'error');
      return;
    }

    if (chat.isLiked === true) {
      chat.showLikeInput = false;
      chat.likeReason = '';
      FeedbackHelper.saveFeedback(comp, chat, null, '', '');
    } else {
      chat.showLikeInput = true;
      chat.showDislikeInput = false;
      (chat as any).showDeleteConfirm = false;
      chat.likeReason = '';
      chat.dislikeReason = '';
      setTimeout(() => {
        if (comp.likeInput) {
          comp.likeInput.nativeElement.focus();
        }
      }, 0);
    }
  }

  static onDislikeClick(comp: any, chat: FeedbackChat) {
    if (!chat.messageId || !comp.selectedChatId) {
      comp.showToast('Cannot save feedback: Message ID is not available', 'error');
      return;
    }

    if (chat.isLiked === false) {
      chat.showDislikeInput = false;
      chat.dislikeReason = '';
      FeedbackHelper.saveFeedback(comp, chat, null, '', '');
    } else {
      chat.showDislikeInput = true;
      chat.showLikeInput = false;
      (chat as any).showDeleteConfirm = false; // Close delete confirm if open
      chat.likeReason = '';
      chat.dislikeReason = '';
      setTimeout(() => {
        if (comp.dislikeInput) {
          comp.dislikeInput.nativeElement.focus();
        }
      }, 0);
    }
  }

  static submitLikeFeedback(comp: any, chat: FeedbackChat) {
    if (!chat.messageId || !comp.selectedChatId) {
      return;
    }

    const reason = chat.likeReason?.trim() || '';
    FeedbackHelper.saveFeedback(comp, chat, true, reason, '');
    chat.showLikeInput = false;
  }

  static cancelLikeFeedback(comp: any, chat: FeedbackChat) {
    chat.showLikeInput = false;
    chat.likeReason = '';
  }

  static submitDislikeFeedback(comp: any, chat: FeedbackChat) {
    if (!chat.messageId || !comp.selectedChatId) {
      return;
    }

    const reason = chat.dislikeReason?.trim() || '';
    FeedbackHelper.saveFeedback(comp, chat, false, '', reason);
    chat.showDislikeInput = false;
  }

  static cancelDislikeFeedback(comp: any, chat: FeedbackChat) {
    chat.showDislikeInput = false;
    chat.dislikeReason = '';
  }

  private static saveFeedback(
    comp: any,
    chat: FeedbackChat,
    isLiked: boolean | null,
    likeReason: string,
    dislikeReason: string
  ) {

    if (!chat.messageId || !comp.selectedChatId) {
      comp.showToast('Cannot save feedback: Message ID is not available', 'error');
      return;
    }

    const previousIsLiked = chat.isLiked;
    const previousFeedbackSaved = chat.feedbackSaved;
    const previousLikeReason = chat.likeReason;
    const previousDislikeReason = chat.dislikeReason;

    chat.isLiked = isLiked;
    chat.feedbackSaved = (isLiked === true || isLiked === false);

    const feedbackRequest = {
      MessageId: chat.messageId,
      IsLiked: isLiked,
      LikeReason: likeReason || '',
      DislikeReason: dislikeReason || ''
    };

    comp.apiService.saveChatFeedback(feedbackRequest).subscribe({
      next: (res: string) => {
        const apiResponse = JSON.parse(res);
        if (apiResponse.HasValidData) {
          chat.likeReason = likeReason;
          chat.dislikeReason = dislikeReason;

          // Add toast message
          let toastMsg = '';
          if (isLiked === true) toastMsg = 'Feedback submitted';
          else if (isLiked === false) toastMsg = 'Feedback submitted';
          else toastMsg = 'Feedback removed';

          comp.showToast(toastMsg, 'success');

          // Update the history list in memory
          if (chat.messageId && comp.selectedChatId) {
            const chatInList = comp.allChatList.find((c: any) => c.ChatId === comp.selectedChatId);
            if (chatInList && chatInList.ChatHistory) {
              const msg = chatInList.ChatHistory.find((h: any) => h.MessageId === chat.messageId);
              if (msg) {
                msg.IsLiked = isLiked;
                (msg as any).LikeReason = likeReason;
                msg.DislikeReason = dislikeReason;
              }
            }
          }
        } else {
          // Revert on failure
          chat.isLiked = previousIsLiked;
          chat.feedbackSaved = previousFeedbackSaved;
          chat.likeReason = previousLikeReason;
          chat.dislikeReason = previousDislikeReason;
          comp.showToast('Failed to save feedback on server', 'error');
        }
      },
      error: (err: any) => {
        // Revert on error
        chat.isLiked = previousIsLiked;
        chat.feedbackSaved = previousFeedbackSaved;
        chat.likeReason = previousLikeReason;
        chat.dislikeReason = previousDislikeReason;
        console.error('An error occurred while saving feedback:', err);
        comp.showToast('Network error while saving feedback', 'error');
      }
    });
  }
}
